import type { LiveBag, LiveBagItem } from "./types";
import { TAGLINE_MAX, parseBadges } from "./productShowcase";

/** A sales live's bag holds this many products at most. */
export const LIVE_BAG_MAX = 10;

export const isLiveBagItem = (v: unknown): v is LiveBagItem => {
  const i = v as Partial<LiveBagItem> | null;
  return (
    !!i &&
    typeof i.productId === "string" &&
    typeof i.title === "string" &&
    typeof i.price === "number" &&
    typeof i.currency === "string" &&
    typeof i.path === "string" &&
    i.path.startsWith("/")
  );
};

/** What a stored item says beyond the basics, kept only when it can be shown:
 *  a higher original price, a sentence, readable tags. */
function cleanExtras(item: LiveBagItem): LiveBagItem {
  const { originalPrice, tagline, tags, ...base } = item;
  const out: LiveBagItem = base;
  if (typeof originalPrice === "number" && originalPrice > base.price) out.originalPrice = originalPrice;
  if (typeof tagline === "string" && tagline.trim()) out.tagline = tagline.trim().slice(0, TAGLINE_MAX);
  const clean = parseBadges({ pill: null, tags })?.tags;
  if (clean?.length) out.tags = clean;
  return out;
}

/**
 * A stored bag with its products as they are now: each one still sellable takes
 * its fresh name, price, photo, sentence and tags, in the bag's own order and
 * pin; one no longer offered is left as it was rather than dropped, so a product
 * paused for a day comes back to the bag without the seller re-adding it.
 */
export function refreshLiveBag(bag: LiveBag, fresh: ReadonlyMap<string, LiveBagItem>): LiveBag {
  return { ...bag, items: bag.items.map((i) => fresh.get(i.productId) ?? i) };
}

/** The bag stored on live_links.bag, or null for none / an unreadable row. A
 *  pinned product that is no longer in the bag is unpinned. */
export function parseLiveBag(raw: string | null | undefined): LiveBag | null {
  if (!raw) return null;
  let v: Partial<LiveBag>;
  try {
    v = JSON.parse(raw) as Partial<LiveBag>;
  } catch {
    return null;
  }
  const items = (Array.isArray(v.items) ? v.items.filter(isLiveBagItem) : [])
    .slice(0, LIVE_BAG_MAX)
    .map(({ sold: _sold, ...item }) => cleanExtras({ ...item, image: item.image ?? null }));
  if (!items.length) return null;
  const pinned = items.some((i) => i.productId === v.pinned) ? (v.pinned as string) : null;
  return { items, pinned };
}

/** Build the bag from the products the seller chose, in their order, keeping
 *  only those still sellable (`known`). The pin must be one of them. */
export function buildLiveBag(
  chosen: readonly string[],
  pinned: string | null,
  known: ReadonlyMap<string, LiveBagItem>,
): LiveBag | null {
  const seen = new Set<string>();
  const items: LiveBagItem[] = [];
  for (const id of chosen) {
    const item = known.get(id);
    if (!item || seen.has(id)) continue;
    seen.add(id);
    items.push(item);
    if (items.length === LIVE_BAG_MAX) break;
  }
  if (!items.length) return null;
  return { items, pinned: pinned && seen.has(pinned) ? pinned : null };
}

/** The bag with each product's paid count in the board's window. */
export function withSold(bag: LiveBag | null, sold: ReadonlyMap<string, number>): LiveBag | null {
  if (!bag) return null;
  return { ...bag, items: bag.items.map((i) => ({ ...i, sold: sold.get(i.productId) ?? 0 })) };
}

/** A product's checkout on the apex: its page (/p/<slug>, or a team's
 *  /<handle>/<slug>), narrowed to that one product. */
export function checkoutPath(page: { slug: string; handle: string | null }, addToCart: string): string {
  const base = page.handle ? `/${page.handle}/${page.slug}` : `/p/${page.slug}`;
  return `${base}/checkout?add-to-cart=${encodeURIComponent(addToCart)}`;
}

/** The sales page a bag item's checkout belongs to (/p/<slug>, /<handle>/<slug>). */
export function salesPagePath(checkout: string): string | null {
  const m = /^(\/[^?#]+?)\/checkout(?:[?#]|$)/.exec(checkout);
  return m ? m[1] : null;
}

/** Where Beli takes a viewer: the checkout, marked as a sale from this live so
 *  the order's campaign touch (page analytics) says where it came from. */
export function liveCheckoutUrl(path: string, liveSlug: string): string {
  const url = new URL(path, "https://live.invalid");
  url.searchParams.set("utm_source", "live");
  url.searchParams.set("utm_medium", "live");
  url.searchParams.set("utm_campaign", `live_${liveSlug}`);
  return url.pathname + url.search;
}

/** Remembered while a viewer checks out from a live, so the thank-you page can
 *  offer the way back after the payment gateway took the whole tab. */
export const LIVE_RETURN_KEY = "shz_live_return";
